"use client";

import { SentryFeatureTags, logException } from "@punchup/sentry";

import type { ShowWithComedian } from "@punchup/data/legacy";
import type { VenueGridItem } from "../../data/venueGridItem";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

async function fetchGroupedShows(
  venuePageId: string
): Promise<VenueGridItem[]> {
  try {
    const res = await getApiClient().get<{ data: VenueGridItem[] }>(
      `/api/v2/public/venues/grouped-shows?venuePageId=${encodeURIComponent(
        venuePageId
      )}`
    );
    return res?.data ?? [];
  } catch (error) {
    logException(error as Error, {
      extra: { data: "Error fetching grouped venue shows" },
      tags: { feature: SentryFeatureTags.VenuePages, venuePageId },
    });
    throw error;
  }
}

function showMatchesQuery(show: ShowWithComedian, q: string): boolean {
  if (show.title?.toLowerCase().includes(q)) return true;
  if (show.metadata_text?.toLowerCase().includes(q)) return true;
  return (
    show.show_comedians?.some((sc) =>
      (sc as { display_name?: string }).display_name?.toLowerCase().includes(q)
    ) ?? false
  );
}

function itemMatchesQuery(item: VenueGridItem, q: string): boolean {
  if (item.type === "single") return showMatchesQuery(item.show, q);
  if (item.group.title.toLowerCase().includes(q)) return true;
  return item.group.shows.some((s) => showMatchesQuery(s, q));
}

interface UseVenuePageGroupedShowsOptions {
  venuePageId: string;
  enabled?: boolean;
  searchTerm?: string;
}

interface UseVenuePageGroupedShowsResult {
  /** Every grid item (single + group), date-ascending. */
  items: VenueGridItem[];
  /** `items` narrowed by the search term (client-side; the full set is loaded). */
  filteredItems: VenueGridItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Grouped-mode counterpart to {@link useVenuePageShows}. Loads the venue's
 * entire grouped grid in ONE request (grouping already done server-side), so
 * there is no offset/limit paging to split a group. Search and "View More" are
 * both display-side over the loaded list, handled by the consumer.
 */
export function useVenuePageGroupedShows({
  venuePageId,
  enabled = true,
  searchTerm = "",
}: UseVenuePageGroupedShowsOptions): UseVenuePageGroupedShowsResult {
  const {
    data: items = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venueGroupedShows", venuePageId],
    queryFn: () => fetchGroupedShows(venuePageId),
    enabled: enabled && !!venuePageId,
    staleTime: 5 * 60 * 1000,
  });

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => itemMatchesQuery(item, q));
  }, [items, searchTerm]);

  return {
    items,
    filteredItems,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
